import { Link } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  Users,
  GraduationCap,
  MessageCircle,
  Calendar,
  Video,
  Award,
  Settings,
  Star,
  CreditCard,
  FileText,
  Shield,
  Mail,
  Clock,
  Zap,
  Heart,
  Globe,
  Lock,
  Bell,
  UserCheck,
  HelpCircle,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  TrendingUp,
  Briefcase,
  Target,
  Sparkles,
  Brain,
  Code,
  DollarSign,
  Share2,
  Download,
  PlayCircle,
  FileQuestion,
  ClipboardCheck,
  Trophy,
  ThumbsUp,
} from "lucide-react";
import SEO from "../components/SEO";

const DocsPage = () => {
  const features = [
    {
      title: "1-on-1 Mentorship",
      description: "Connect with industry experts for personalized guidance. Get career advice, technical mentorship, and interview preparation from professionals at top companies.",
      icon: GraduationCap,
      link: "/mentorship",
      color: "from-orange-500 to-orange-600"
    },
    {
      title: "Expert-Led Courses",
      description: "Learn from experienced instructors with structured courses. Master new skills with video lessons, quizzes, assignments, and real-world projects.",
      icon: BookOpen,
      link: "/courses",
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Live Video Sessions",
      description: "Join interactive video calls with Google Meet integration. Record sessions, share screens, and get real-time feedback from mentors and instructors.",
      icon: Video,
      link: "/mentorship",
      color: "from-green-500 to-green-600"
    },
    {
      title: "Community Forums",
      description: "Engage with a global community of learners. Ask questions, share knowledge, collaborate on projects, and build your professional network.",
      icon: MessageCircle,
      link: "/forums",
      color: "from-purple-500 to-purple-600"
    },
    {
      title: "Verified Certificates",
      description: "Earn shareable certificates upon course completion. Boost your resume, LinkedIn profile, and career opportunities with verified credentials.",
      icon: Award,
      link: "/my-certificates",
      color: "from-yellow-500 to-yellow-600"
    },
    {
      title: "Progress Tracking",
      description: "Monitor your learning journey with detailed analytics. Track completed lessons, quiz scores, course progress, and achievement milestones.",
      icon: TrendingUp,
      link: "/dashboard",
      color: "from-pink-500 to-pink-600"
    }
  ];

  const stats = [
    { value: "50+", label: "Expert-Led Courses", icon: BookOpen, description: "Continuously updated curriculum" },
    { value: "100+", label: "Verified Mentors", icon: Users, description: "From top tech companies" },
    { value: "10,000+", label: "Active Students", icon: Heart, description: "Growing community" },
    { value: "98%", label: "Satisfaction Rate", icon: Star, description: "Based on 5,000+ reviews" },
    { value: "30+", label: "Countries", icon: Globe, description: "Worldwide reach" },
    { value: "500+", label: "Success Stories", icon: Trophy, description: "Career transformations" },
  ];

  const quickLinks = [
    { title: "Browse Courses", icon: BookOpen, link: "/courses", description: "Explore our catalog" },
    { title: "Find a Mentor", icon: Users, link: "/mentorship", description: "Get expert guidance" },
    { title: "Join Discussions", icon: MessageCircle, link: "/forums", description: "Ask questions" },
    { title: "My Dashboard", icon: Settings, link: "/dashboard", description: "Track progress" },
  ];

  const sections = [
    {
      id: "overview",
      title: "Platform Overview",
      icon: Globe,
      content: `LearnHub is a comprehensive e-learning ecosystem designed to transform how people learn and grow professionally. Our platform bridges the gap between aspiring learners and industry experts through structured courses, personalized mentorship, and collaborative community features. Whether you're looking to start a new career, advance in your current role, or explore a passion, LearnHub provides the tools, guidance, and support you need to succeed.`,
      subContent: `Since our launch, we've helped thousands of students achieve their learning goals, with mentors and instructors from leading companies like Google, Microsoft, Amazon, and Meta. Our approach combines high-quality video content, interactive assessments, real-world projects, and direct access to experts who have been where you want to go.`,
      link: "/about",
      linkText: "Learn more about our mission"
    },
    {
      id: "roles",
      title: "User Roles & Access",
      icon: Users,
      intro: "LearnHub supports four distinct user roles, each with tailored features and permissions to create the best experience for every type of user.",
      subSections: [
        {
          title: "🎓 Student",
          description: "Students are at the heart of LearnHub. Access courses, book mentorship sessions, participate in forums, and track your learning journey.",
          features: [
            "Browse and enroll in 50+ expert-led courses",
            "Book 1-on-1 mentorship sessions with industry professionals",
            "Join community forums and discussion groups",
            "Earn verified certificates upon course completion",
            "Track progress with detailed analytics dashboard",
            "Save courses to wishlist for later",
            "Leave reviews and ratings for courses and mentors"
          ],
          link: "/register?role=student",
          linkText: "Join as a Student"
        },
        {
          title: "👨‍🏫 Mentor",
          description: "Share your expertise, help others grow, and build your personal brand. Mentors provide 1-on-1 guidance through video sessions.",
          features: [
            "Create a professional mentor profile",
            "Set your availability and session pricing",
            "Accept or decline session requests",
            "Chat with students before and after sessions",
            "Receive ratings and written reviews",
            "Track your earnings and session history",
            "Build your reputation and personal brand"
          ],
          link: "/become-mentor",
          linkText: "Become a Mentor"
        },
        {
          title: "📚 Instructor",
          description: "Create and sell courses, build your audience, and generate passive income by sharing your knowledge at scale.",
          features: [
            "Create structured courses with modules and lessons",
            "Add video, text, quiz, and assignment content",
            "Set your own pricing and earn revenue",
            "Review student submissions and provide feedback",
            "Track course performance and student progress",
            "Issue certificates to completing students",
            "Build a following and establish authority"
          ],
          link: "/become-instructor",
          linkText: "Become an Instructor"
        },
        {
          title: "🔧 Admin",
          description: "Platform administrators have full control over users, content, and system settings to ensure smooth operation.",
          features: [
            "Manage all users and their roles",
            "Approve mentor and instructor applications",
            "View platform-wide analytics and reports",
            "Handle user reports and content moderation",
            "Configure platform settings and features",
            "Manage course categories and tags",
            "Process refunds and handle disputes"
          ],
          link: "/admin/dashboard",
          linkText: "Admin Dashboard"
        }
      ]
    },
    {
      id: "getting-started",
      title: "Getting Started Guide",
      icon: Sparkles,
      intro: "New to LearnHub? Follow these simple steps to begin your learning journey and make the most of everything our platform has to offer.",
      steps: [
        { 
          step: "01", 
          title: "Create Your Account", 
          description: "Sign up with your email address or social media account. Choose your primary role (Student, Mentor, or Instructor) – you can always add more roles later. Verify your email to activate your account and unlock all features.",
          duration: "2 minutes",
          icon: UserCheck
        },
        { 
          step: "02", 
          title: "Complete Your Profile", 
          description: "Add a profile photo, write a compelling bio, and connect your social media links. A complete profile helps you build trust with mentors, instructors, and fellow students. For mentors and instructors, this is especially important for attracting students.",
          duration: "5 minutes",
          icon: Settings
        },
        { 
          step: "03", 
          title: "Explore the Platform", 
          description: "Browse our course catalog, explore mentor profiles, and check out community forums. Use filters to find content that matches your interests, skill level, and goals. Save interesting courses to your wishlist for later.",
          duration: "10 minutes",
          icon: Globe
        },
        { 
          step: "04", 
          title: "Start Learning", 
          description: "Enroll in a course or book a mentorship session. Watch lessons at your own pace, complete quizzes and assignments, and track your progress. For mentorship, prepare your questions and goals before the session to make the most of your time.",
          duration: "Ongoing",
          icon: PlayCircle
        },
        { 
          step: "05", 
          title: "Earn Certificates", 
          description: "Complete courses to earn verified certificates. Share them on LinkedIn, add them to your resume, and showcase your achievements to employers. Certificates are permanently stored in your profile.",
          duration: "Varies by course",
          icon: Award
        }
      ]
    },
    {
      id: "mentorship",
      title: "Mentorship Program",
      icon: Target,
      intro: "Our mentorship program connects you with industry professionals who provide personalized guidance, career advice, and technical mentorship. Here's everything you need to know about finding, booking, and making the most of mentorship sessions.",
      subSections: [
        {
          title: "Finding Your Perfect Mentor",
          description: "With 100+ verified mentors from top companies, finding the right fit is easy. Use filters to narrow down by expertise, industry, pricing, and availability. Read reviews from other students to learn about their experiences.",
          steps: [
            "Visit the Mentorship directory at /mentorship",
            "Use filters to search by expertise, price range, or rating",
            "Browse mentor profiles with detailed bios and reviews",
            "Click on a mentor to view their full profile and availability",
            "Book a free introductory call (if offered) to ensure good fit"
          ],
          tips: [
            "Look for mentors whose career path matches your goals",
            "Read reviews to understand the mentor's teaching style",
            "Check availability to ensure it fits your schedule",
            "Start with a single session to test compatibility"
          ],
          link: "/mentorship",
          linkText: "Find Your Mentor"
        },
        {
          title: "Booking a Session",
          description: "Once you've found a mentor, booking a session is straightforward. Choose a time that works for you, add session details, and complete payment for paid sessions.",
          steps: [
            "Click 'Book Session' on the mentor's profile",
            "Select a date and time from their available slots",
            "Enter the session topic and any specific questions or goals",
            "Review the session details and pricing",
            "Complete payment (for paid sessions) using secure payment gateway",
            "Wait for mentor confirmation (usually within 24 hours)"
          ],
          tips: [
            "Book at least 3-7 days in advance for best availability",
            "Prepare specific questions or topics before booking",
            "For career coaching, share your resume beforehand",
            "For technical mentorship, share code or project links"
          ],
          link: "/mentorship",
          linkText: "Book a Session"
        },
        {
          title: "Preparing for Your Session",
          description: "Preparation is key to a successful mentorship session. Take time to organize your thoughts, gather relevant materials, and set clear goals for what you want to achieve.",
          steps: [
            "Review the mentor's bio and expertise areas",
            "Write down specific questions or topics to discuss",
            "Share relevant materials beforehand (resume, code, portfolio)",
            "Test your camera, microphone, and internet connection",
            "Join the Google Meet link a few minutes early",
            "Take notes during the session for future reference"
          ],
          tips: [
            "Be specific about what you want help with",
            "Share your goals and expectations upfront",
            "Record the session (with permission) for review",
            "Ask for actionable next steps or resources"
          ],
          link: "/mentorship/guide",
          linkText: "Session Preparation Guide"
        },
        {
          title: "After the Session",
          description: "The relationship doesn't end when the session does. Follow up, apply what you learned, and stay connected with your mentor.",
          steps: [
            "Review your notes and action items",
            "Apply feedback and work on suggested improvements",
            "Follow up with your mentor via chat if you have questions",
            "Leave a review to help other students choose mentors",
            "Book follow-up sessions to continue your progress"
          ],
          tips: [
            "Implement at least one action item before your next session",
            "Share your progress with your mentor to stay accountable",
            "Refer mentors to friends and colleagues who need help",
            "Build a long-term relationship for ongoing guidance"
          ],
          link: "/dashboard/messages",
          linkText: "Message Your Mentor"
        }
      ]
    },
    {
      id: "courses",
      title: "Courses & Learning",
      icon: Brain,
      intro: "Our course catalog features expert-led courses across web development, data science, design, business, and more. Each course is structured with modules, lessons, quizzes, and assignments to ensure comprehensive learning.",
      subSections: [
        {
          title: "Finding the Right Course",
          description: "With 50+ courses across multiple categories, finding the right one is easy. Browse by category, difficulty level, duration, or rating.",
          features: [
            "Browse all courses at /courses",
            "Filter by category (Web Dev, Data Science, Design, Business)",
            "Sort by rating, popularity, or newest",
            "Search by keyword or topic",
            "View course previews and syllabus before enrolling",
            "Read reviews from other students"
          ],
          link: "/courses",
          linkText: "Explore Courses"
        },
        {
          title: "Course Structure",
          description: "Each course is carefully structured to guide you from beginner to proficient. Here's what you can expect:",
          steps: [
            "Modules: Major topic sections with clear learning objectives",
            "Lessons: Video or text-based content with examples",
            "Quizzes: Test your understanding with multiple-choice questions",
            "Assignments: Practical projects to apply your skills",
            "Resources: Downloadable materials and code samples",
            "Certificate: Awarded upon completing all requirements"
          ],
          link: "/courses",
          linkText: "View Sample Course"
        },
        {
          title: "Learning Flow",
          description: "Follow this proven path to successfully complete courses and retain what you learn:",
          steps: [
            "Watch each lesson video actively (take notes, pause to practice)",
            "Complete lesson quizzes to reinforce understanding",
            "Do hands-on exercises and coding challenges",
            "Submit assignments for instructor feedback",
            "Participate in course discussions and Q&A",
            "Review and retake quizzes as needed",
            "Complete final project or exam (if applicable)",
            "Download and share your certificate"
          ],
          tips: [
            "Study consistently: 30-60 minutes daily is more effective than cramming",
            "Join the course forum to connect with other students",
            "Re-watch lessons as needed for complex topics",
            "Build projects beyond the assignments to reinforce learning"
          ],
          link: "/my-learning",
          linkText: "My Learning Dashboard"
        }
      ]
    },
    {
      id: "certificates",
      title: "Certificates & Achievements",
      icon: Trophy,
      content: "Upon completing a course, you'll receive a verified certificate that you can download, print, and share. Our certificates include a unique ID that employers can verify online.",
      features: [
        "PDF certificate with secure verification link",
        "Share directly to LinkedIn profile",
        "Download for printing or portfolio",
        "Permanent storage in your account",
        "Employers can verify authenticity online"
      ],
      link: "/my-certificates",
      linkText: "View Your Certificates"
    },
    {
      id: "community",
      title: "Community & Forums",
      icon: MessageCircle,
      intro: "Learning is better together. Our community forums are where students, mentors, and instructors connect, collaborate, and help each other grow.",
      subSections: [
        {
          title: "Forum Categories",
          description: "Find discussions organized by topic to easily find answers and contribute your knowledge.",
          features: [
            "Technical Help: Get coding and technical assistance",
            "General Discussion: Chat about learning and careers",
            "Project Showcase: Share your work and get feedback",
            "Resources: Share helpful articles, tools, and tutorials",
            "Announcements: Platform news and updates"
          ],
          link: "/forums",
          linkText: "Visit Forums"
        },
        {
          title: "Best Practices",
          description: "Get the most out of community forums with these guidelines:",
          steps: [
            "Search before posting (your question may already be answered)",
            "Use clear, descriptive titles for your posts",
            "Format code blocks properly for readability",
            "Be respectful and constructive in responses",
            "Mark helpful answers as solutions",
            "Thank people who help you"
          ],
          link: "/forums/guidelines",
          linkText: "Community Guidelines"
        }
      ]
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting & FAQ",
      icon: HelpCircle,
      intro: "Encountering issues? Check these common problems and solutions before contacting support.",
      issues: [
        { problem: "Can't log in to my account", solution: "Verify your email address. Use 'Forgot Password' to reset. Clear browser cache. Check if account is blocked. Contact support if issue persists." },
        { problem: "Video lessons won't play", solution: "Check internet connection. Try different browser. Clear cache. Disable ad blockers. Update browser to latest version." },
        { problem: "Mentorship session not confirmed", solution: "Mentor may take up to 24 hours to confirm. Check your booking status. Contact mentor via chat. Ensure payment was successful." },
        { problem: "Certificate not generating", solution: "Ensure all course requirements are met (100% progress). Wait up to 10 minutes after completion. Refresh dashboard. Contact support." },
        { problem: "Payment failed", solution: "Check card details. Ensure sufficient funds. Try different payment method. Contact your bank. Use alternative payment gateway." },
        { problem: "Can't access course content", solution: "Verify enrollment status. Ensure course is published. Check if payment was successful. Contact instructor." },
        { problem: "Quiz not scoring correctly", solution: "Ensure all questions answered. Check for correct answer format. Refresh and retry. Contact instructor if issue persists." }
      ],
      link: "/contact",
      linkText: "Still need help? Contact Support"
    }
  ];

  return (
    <>
      <SEO
        title="LearnHub Documentation - Guides & Tutorials"
        description="Complete guides and tutorials for LearnHub platform. Learn how to use courses, mentorship, forums, certificates, and more. Get started with step-by-step instructions."
        keywords="learnhub documentation, user guide, tutorials, how to use, mentorship guide, course platform, learning management"
      />
      <main className="min-h-screen pt-16 pb-16 bg-gradient-to-b from-orange-50/30 via-white to-white">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="py-8 text-center lg:py-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-medium text-orange-600 bg-orange-100 rounded-full">
              <HelpCircle className="w-4 h-4" />
              Documentation Center
            </div>
            <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
              Welcome to{" "}
              <span className="text-transparent bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text">
                LearnHub
              </span>
            </h1>
            <p className="max-w-2xl mx-auto mt-4 text-base text-gray-600 sm:text-lg">
              Your complete guide to mastering the platform — from getting
              started to advanced features
            </p>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-3 mb-10 sm:grid-cols-4 sm:gap-4">
            {quickLinks.map((link, idx) => (
              <Link
                key={idx}
                to={link.link}
                className="group p-3 bg-white border border-gray-100 rounded-xl text-center hover:shadow-md transition-all hover:-translate-y-0.5"
              >
                <link.icon className="w-5 h-5 mx-auto mb-2 text-orange-500 transition-transform group-hover:scale-110" />
                <p className="text-sm font-medium text-gray-900">
                  {link.title}
                </p>
                <p className="text-xs text-gray-400">{link.description}</p>
              </Link>
            ))}
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 gap-3 mb-12 sm:grid-cols-3 lg:grid-cols-6 sm:gap-4">
            {stats.map((stat, idx) => (
              <div
                key={idx}
                className="p-3 text-center bg-white border border-gray-100 shadow-sm rounded-xl"
              >
                <stat.icon className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                <p className="text-lg font-bold text-gray-900 sm:text-xl">
                  {stat.value}
                </p>
                <p className="text-xs font-medium text-gray-700">
                  {stat.label}
                </p>
                <p className="text-xs text-gray-400">{stat.description}</p>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="mb-12">
            <div className="mb-6 text-center">
              <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Everything You Need to Succeed
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Powerful features designed to accelerate your learning journey
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, idx) => (
                <Link
                  key={idx}
                  to={feature.link}
                  className="p-5 transition-all bg-white border border-gray-100 shadow-sm group rounded-xl hover:shadow-md hover:-translate-y-1"
                >
                  <div
                    className={`w-12 h-12 mb-4 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center shadow-sm`}
                  >
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="mb-2 text-base font-bold text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="mb-4 text-sm leading-relaxed text-gray-600">
                    {feature.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-orange-600 transition-all group-hover:gap-2">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Main Documentation Sections */}
          <div className="space-y-6">
            {sections.map((section) => (
              <div
                key={section.id}
                id={section.id}
                className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl"
              >
                {/* Section Header */}
                <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-xl">
                      <section.icon className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 sm:text-xl">
                        {section.title}
                      </h2>
                      {section.intro && (
                        <p className="mt-1 text-sm text-gray-600">
                          {section.intro}
                        </p>
                      )}
                    </div>
                  </div>
                  {section.content && (
                    <div className="mt-3 text-sm leading-relaxed text-gray-700">
                      <p>{section.content}</p>
                      {section.subContent && (
                        <p className="mt-2">{section.subContent}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Section Body */}
                <div className="p-5 sm:p-6">
                  {/* Features Grid */}
                  {section.features && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {section.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50/30"
                        >
                          <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Steps */}
                  {section.steps && (
                    <div className="space-y-5">
                      {section.steps.map((step, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 text-sm font-bold text-orange-600 bg-orange-100 rounded-xl">
                              {step.step}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-base font-semibold text-gray-900">
                                {step.title}
                              </h3>
                              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                {step.duration}
                              </span>
                            </div>
                            <p className="text-sm leading-relaxed text-gray-600">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* SubSections */}
                  {section.subSections && (
                    <div className="space-y-6">
                      {section.subSections.map((sub, idx) => (
                        <div
                          key={idx}
                          className="pb-5 border-b border-gray-100 last:border-0 last:pb-0"
                        >
                          <h3 className="mb-2 text-base font-semibold text-gray-900">
                            {sub.title}
                          </h3>
                          <p className="mb-3 text-sm leading-relaxed text-gray-600">
                            {sub.description}
                          </p>

                          {sub.steps && (
                            <div className="mb-3">
                              <p className="mb-2 text-sm font-medium text-gray-800">
                                📋 Steps:
                              </p>
                              <ul className="space-y-1.5">
                                {sub.steps.map((step, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-gray-600"
                                  >
                                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                                      <span className="text-xs font-bold text-orange-600">
                                        {i + 1}
                                      </span>
                                    </div>
                                    <span>{step}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {sub.tips && (
                            <div className="p-3 mb-3 rounded-lg bg-blue-50">
                              <p className="mb-2 text-sm font-medium text-blue-800">
                                💡 Pro Tips:
                              </p>
                              <ul className="space-y-1">
                                {sub.tips.map((tip, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-blue-700"
                                  >
                                    <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{tip}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {sub.features && (
                            <div className="mb-3">
                              <ul className="space-y-1.5">
                                {sub.features.map((feature, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-gray-600"
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {sub.link && (
                            <Link
                              to={sub.link}
                              className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-orange-600 transition-all hover:gap-3"
                            >
                              {sub.linkText || "Get Started"}{" "}
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Issues */}
                  {section.issues && (
                    <div className="space-y-3">
                      {section.issues.map((issue, idx) => (
                        <div
                          key={idx}
                          className="flex gap-3 p-3 rounded-lg bg-yellow-50"
                        >
                          <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {issue.problem}
                            </p>
                            <p className="mt-1 text-sm text-gray-600">
                              {issue.solution}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Section Link */}
                  {section.link && (
                    <div className="pt-4 mt-5 border-t border-gray-100">
                      <Link
                        to={section.link}
                        className="inline-flex items-center gap-2 text-sm font-medium text-orange-600 transition-all hover:gap-3"
                      >
                        {section.linkText || `Explore ${section.title}`}
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Help Section */}
          <div className="p-6 mt-10 text-center border border-orange-100 bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-xl">
            <div className="inline-flex items-center justify-center mb-4 bg-white rounded-full shadow-sm w-14 h-14">
              <Mail className="w-6 h-6 text-orange-500" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-gray-900">
              Still Have Questions?
            </h3>
            <p className="max-w-md mx-auto mb-4 text-sm text-gray-600">
              Can't find what you're looking for? Our support team is here to
              help you 24/7.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Contact Support
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                Visit FAQ
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};
export default DocsPage;